// @flow
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { formatConsumption, formatCurrency } from "modules/Offers/misc/Utils";
import { scrollToElement } from "Commons/Utilities/scrollTo";
import { isMobile } from "Commons/Utilities";
import { LocalStorage } from "Commons/Utilities/storage";

import type { OfferT } from "modules/Offers/misc/Types";
import type { ReminderT } from "modules/Reminder/misc/Types";

import Styles from "./OfferSummary.module.scss";

type Props = {
  reminder: ?ReminderT,
  offer: ?OfferT,
  email: string,
  onClick?: Function,
};

const OfferSummary = ({ reminder, offer, email, onClick }: Props) => {
  const { t } = useTranslation("offerPageDetail");
  const remindMeBonus = offer?.remindMeBonus;
  const refContainter = useRef();
  const lpColor = LocalStorage.get("lpColor") || "#328ed3";

  useEffect(() => {
    if (isMobile()) scrollToElement(refContainter.current, "smooth", -60);
  }, []);

  const _savings = remindMeBonus?.amount ? remindMeBonus.totalSaving : offer?.gasElectricityDetails?.savings || 0;

  const savings =
    _savings <= 0 ? (
      "Aktuell keine Ersparnis"
    ) : (
      <>
        <span>{t("offerBox.savings")}</span>
        <span>{formatCurrency(_savings)}</span>
      </>
    );

  return (
    <div
      ref={refContainter}
      className={Styles.offerSummary}
      style={{ backgroundImage: `linear-gradient(180deg, #fff 0%, ${lpColor} 100%)` }}
    >
      <div className={Styles.offerTable}>
        <h4>{`${t("offerBox.title")}: ${reminder?.shortOfferOrderNumber || ""}`}</h4>
        <div className={Styles.offerSummaryRow}>
          <span>{t("offerBox.consumption")}</span>
          <span>{formatConsumption(reminder)}</span>
        </div>
        <div className={Styles.offerSummaryRow}>
          <span>{t("offerBox.postalCode")}</span>
          <span>{reminder?.postalcode}</span>
        </div>
        <div className={Styles.offerSummaryRow}>
          <span>{t("offerBox.provider")}</span>
          <span>
            {offer?.gasElectricityDetails?.providerLogoUrl ? (
              <img
                src={offer?.gasElectricityDetails?.providerLogoUrl}
                alt={offer?.gasElectricityDetails?.providerName}
              />
            ) : (
              offer?.gasElectricityDetails?.providerName
            )}
          </span>
        </div>
        <div className={Styles.offerSummaryRow}>
          <span>{t("offerBox.mail")}</span>
          <span>{email}</span>
        </div>
        <div className={Styles.offerSummaryRow}>
          <span className={Styles.oldPrice}>{t("offerBox.previousYearPrice")}</span>
          <span>{formatCurrency(offer?.gasElectricityDetails?.benchmarkTariffTotalCostInEuro || 0)}</span>
        </div>
        <div className={Styles.offerSummaryRow}>
          <span className={Styles.newPrice}>{t("offerBox.ourYearPrice")}</span>
          <span>
            {formatCurrency(
              remindMeBonus?.amount
                ? remindMeBonus.totalRunningCostAmount
                : offer?.gasElectricityDetails?.totalCostAmount || 0
            )}
          </span>
        </div>
        <div className={Styles.offerSummaryRow}>{savings}</div>
      </div>
    </div>
  );
};

export default OfferSummary;
